import * as React from 'react';
import axios, { AxiosResponse } from 'axios';
import {
  CapEnvironmentService,
  useCapEnvironmentsService,
} from '@cengage/cap-portal-environment-lib-react';
import LRU from 'lru-cache';

import {
  CourseEnrollment,
  CourseList,
  CourseUri,
} from './portal-core-data.model';

export enum CoreDataEnvironmentKeys {
  CoreDataEndpoint = 'coreDataEndpoint',
  CoreDataCacheEnabled = 'coreDataCacheEnabled',
}

export class PortalCoreDataService {
  private courses?: CourseList;
  private readonly cache = new LRU({ max: 10 });

  constructor(private environmentService: CapEnvironmentService) {}

  loadCourses(): Promise<CourseList> {
    if (this.courses && this.cacheEnabled()) {
      return Promise.resolve(this.courses);
    } else {
      return axios
        .get(`${this.coreDataEndpoint()}/courses`)
        .then((cl: AxiosResponse<CourseList>) => {
          this.courses = cl.data;
          return cl.data;
        });
    }
  }

  loadCourseEnrollment(courseUri: CourseUri): Promise<CourseEnrollment> {
    if (this.cache.has(courseUri) && this.cacheEnabled()) {
      return Promise.resolve(this.cache.get(courseUri)!);
    } else {
      return axios
        .get(
          `${this.coreDataEndpoint()}/course-enrollment/${encodeURIComponent(
            courseUri
          )}`
        )
        .then((ce: AxiosResponse<CourseEnrollment>) => {
          this.cache.set(courseUri, ce.data);
          return ce.data;
        });
    }
  }

  private coreDataEndpoint(): string {
    return this.environmentService.getEnvironmentSetting(
      CoreDataEnvironmentKeys.CoreDataEndpoint
    );
  }

  private cacheEnabled(): boolean {
    return (
      this.environmentService.getEnvironmentSetting(
        CoreDataEnvironmentKeys.CoreDataCacheEnabled
      ) === 'true'
    );
  }
}

export const CORE_DATA_SERVICE = React.createContext<PortalCoreDataService>(
  null!
);

export function CoreDataServiceProvider({ children }: React.PropsWithChildren) {
  const environmentService = useCapEnvironmentsService();
  const [coreDataService] = React.useState(
    () => new PortalCoreDataService(environmentService)
  );

  return (
    <CORE_DATA_SERVICE.Provider value={coreDataService}>
      {children}
    </CORE_DATA_SERVICE.Provider>
  );
}

export function useCoreDataService() {
  return React.useContext(CORE_DATA_SERVICE);
}
